import { SkeletonLoader } from '@toggle/design-system';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useThemePerformanceReturn } from '../hooks/useThemePerformanceReturn';
import styles from './HorizonItem.module.scss';

export type Horizon = '1D' | '1W' | '1M' | '3M' | '6M' | '1Y';

interface HorizonItemProps {
  horizonToIndexMap: Map<string, number>;
  horizon: Horizon;
  isSelected: boolean;
  benchmarkTags?: string[];
  onClick: () => void;
  themeId: string;
}

export const HorizonItem = ({
  benchmarkTags,
  horizon,
  horizonToIndexMap,
  isSelected,
  onClick,
  themeId,
}: HorizonItemProps) => {
  const { t } = useTranslation(['widget', 'scenario']);

  const {
    value: revampValue,
    isNegative: revampIsNegative,
    isLoading: revampIsLoading,
  } = useThemePerformanceReturn({
    themeId,
    selectedHorizon: horizon,
    benchmarkTags,
    numDays: horizonToIndexMap.get(horizon.toLowerCase()),
  });

  const dataToUse = {
    value: revampValue,
    isNegative: revampIsNegative,
    isLoading: revampIsLoading,
  };

  const horizonTranslationMap: Record<Horizon, string> = {
    '1D': t('scenario:filters.horizons.1d'),
    '1W': t('scenario:filters.horizons.1w'),
    '1M': t('scenario:filters.horizons.1m'),
    '3M': t('scenario:filters.horizons.3m'),
    '6M': t('scenario:filters.horizons.6m'),
    '1Y': t('scenario:filters.horizons.1y'),
  };

  return (
    <div
      className={clsx(styles.horizonWrapper, {
        [styles.selected]: isSelected,
      })}
      onClick={onClick}
    >
      <span className={styles.horizonTitle}>
        {horizonTranslationMap[horizon]}
      </span>
      <span className={styles.secondRowWrapper}>
        {benchmarkTags && (
          <span className={styles.horizonPrefix}>
            {t(
              `widget:themeOverview.tabs.overview.${
                dataToUse.isNegative ? 'horizonPrefixUp' : 'horizonPrefixOp'
              }`
            )}
          </span>
        )}
        {dataToUse.isLoading ? (
          <div className={styles.skeletonLoaderWrapper}>
            <SkeletonLoader
              areas={[
                {
                  styles: {
                    width: '50px',
                    height: '20px',
                    borderRadius: '8px',
                  },
                },
              ]}
            />
          </div>
        ) : (
          <span
            className={clsx(styles.horizonValue, {
              [styles.negative]: dataToUse.isNegative,
            })}
          >
            {dataToUse.value}
          </span>
        )}
      </span>
    </div>
  );
};
